import React from 'react';
import {
    Box,
    Typography,
    Chip,
} from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Check,
    EmojiObjects,
    Warning,
    Psychology,
    QuestionAnswer,
    TrendingUp,
} from '@mui/icons-material';
import { TestResult } from '../types';

interface ResultSectionProps {
    result: TestResult;
    variant: 'old' | 'new';
}

export const ResultSection: React.FC<ResultSectionProps> = ({ result, variant }) => {
    const isNew = variant === 'new';

    return (
        <Box
            sx={{
                height: '100%',
                ...(isNew && {
                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                    p: 2,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'rgba(25, 118, 210, 0.2)',
                }),
            }}
        >
            {/* Recommendation Tag */}
            {result.recommendation && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={result.recommendation}
                        color={result.recommendation === 'RECOMMENDED' ? 'success' : 'error'}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            borderRadius: 1,
                            height: 'auto',
                            '& .MuiChip-label': {
                                display: 'block',
                                whiteSpace: 'normal',
                                py: 0.5,
                            },
                        }}
                    />
                </Box>
            )}

            {/* Reasoning */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmojiObjects sx={{ fontSize: 18, color: 'primary.main' }} />
                    Final Reasoning
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {result.reasoning}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
                {/* Match Strengths */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                        Match Strengths
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {result.matchStrengths || 'No data available.'}
                    </Typography>
                </Box>

                {/* Match Gaps */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Cancel sx={{ fontSize: 18, color: 'error.main' }} />
                        Match Gaps
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {result.matchGaps || 'No data available.'}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
                {/* Growth Potential */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TrendingUp sx={{ fontSize: 18, color: 'success.main' }} />
                        Growth Potential
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {result.growthPotential || 'No data available.'}
                    </Typography>
                </Box>

                {/* Risk Factors */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Warning sx={{ fontSize: 18, color: 'warning.main' }} />
                        Risk Factors
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {result.riskFactors || 'No data available.'}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
                {/* Cultural Fit */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Psychology sx={{ fontSize: 18, color: 'primary.main' }} />
                        Cultural Fit
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {result.culturalFit || 'No data available.'}
                    </Typography>
                </Box>

                {/* Interview Focus */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <QuestionAnswer sx={{ fontSize: 18, color: 'primary.main' }} />
                        Interview Focus
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {result.interviewFocus || 'No data available.'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
